import { useState, useEffect } from 'react';

/**
 * Reliable "installed PWA" (standalone) detection.
 *
 * iOS home-screen web apps do NOT reliably match
 * `@media (display-mode: standalone)`; their authoritative signal is
 * `navigator.standalone === true`. Android/desktop installed PWAs match the
 * media query. Checking BOTH is the only way to detect standalone on every
 * platform. The app previously relied on the media query alone, so every
 * standalone-gated rule silently no-oped on iPhone.
 */
export function getIsStandalone() {
  if (typeof window === 'undefined') return false;
  const mediaMatch =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return Boolean(mediaMatch || window.navigator.standalone === true);
}

export default function useIsStandalone() {
  const [standalone, setStandalone] = useState(getIsStandalone);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia('(display-mode: standalone)');
    const onChange = () => setStandalone(getIsStandalone());
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return standalone;
}
